import { forwardRef, type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { repo2FinanceV2ClassNames } from '../../styles/repo2Brand';

interface FinanceV2SectionProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  style?: CSSProperties;
  tabIndex?: number;
}

/**
 * Repo-2 branding pass:
 * Local section chrome for Smart Insights dashboard blocks. This mirrors Repo 2
 * panel structure without introducing shared/global styles that could affect v1.
 */
export const FinanceV2Section = forwardRef<HTMLElement, FinanceV2SectionProps>(
  ({ title, subtitle, icon, actions, children, className, contentClassName, style, tabIndex }, ref) => (
    <section
      ref={ref}
      tabIndex={tabIndex}
      style={style}
      className={cn(
        'relative min-w-0 overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white/85 text-[#1F1A20]',
        'shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)] backdrop-blur-[2px]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6E3450]/25',
        className
      )}
    >
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 border-b border-[#EDE5D8] px-5 pb-3 pt-4 md:px-6">
          <div className="flex min-w-0 items-center gap-2.5">
            {icon}
            {title && <span className={cn(repo2FinanceV2ClassNames.eyebrow, 'truncate')}>{title}</span>}
            {subtitle && (
              <span className="truncate text-[12px] font-normal normal-case tracking-normal text-[#7B6B72]">
                {subtitle}
              </span>
            )}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </header>
      )}
      <div className={cn('min-w-0 px-5 py-4 md:px-6', contentClassName)}>{children}</div>
    </section>
  )
);

FinanceV2Section.displayName = 'FinanceV2Section';
